class CreateImageCommand {
    constructor(params, env) {
        this.width = params.width;
        this.height = params.height;
        this.preview = env.three.renderer.domElement;
        this.editorType = env.params.editorType;
        this.studio = env.params.studio || {};
        this.studioPreset = this.normalizeStudioPreset(this.studio.backgroundType);
        this.studioBackground = this.studio.backgroundImage || '';
        this.studioBackgroundAdjust = this.normalizeStudioBackgroundAdjust(this.studio.backgroundAdjust);
    }
    clampNumber(value, min, max, fallback) {
        const numberValue = Number(value);
        if (!Number.isFinite(numberValue)) {
            return fallback;
        }
        return Math.min(max, Math.max(min, numberValue));
    }
    normalizeStudioPreset(preset) {
        return ['white', 'warm', 'gray', 'dark'].includes(preset) ? preset : 'warm';
    }
    normalizeStudioBackgroundAdjust(config = {}) {
        return {
            x: this.clampNumber(config.x, -300, 300, 0),
            y: this.clampNumber(config.y, -300, 300, 0),
            scale: this.clampNumber(config.scale, 0.8, 2, 1),
            brightness: this.clampNumber(config.brightness, 45, 160, 100),
            contrast: this.clampNumber(config.contrast, 50, 150, 100),
        };
    }
    getStudioPalette() {
        const palettes = {
            white: {
                wallTop: '#ffffff',
                wallBottom: '#fbfcfb',
                floorTop: '#eef0ed',
                floorBottom: '#e4e7e2',
                light: 'rgba(255, 255, 255, 0.86)',
                shadow: 'rgba(74, 80, 72, 0.12)',
            },
            warm: {
                wallTop: '#f6f4ef',
                wallBottom: '#f8f7f3',
                floorTop: '#e5ddd0',
                floorBottom: '#d8cdbc',
                light: 'rgba(255, 255, 255, 0.78)',
                shadow: 'rgba(84, 69, 52, 0.16)',
            },
            gray: {
                wallTop: '#f2f4f5',
                wallBottom: '#eef1f3',
                floorTop: '#d7dde2',
                floorBottom: '#c8d0d7',
                light: 'rgba(255, 255, 255, 0.68)',
                shadow: 'rgba(50, 60, 68, 0.16)',
            },
            dark: {
                wallTop: '#3e4247',
                wallBottom: '#2d3035',
                floorTop: '#222427',
                floorBottom: '#15171a',
                light: 'rgba(255, 255, 255, 0.15)',
                shadow: 'rgba(0, 0, 0, 0.32)',
            },
        };
        return palettes[this.studioPreset] || palettes.warm;
    }
    loadImage(url) {
        return new Promise((resolve) => {
            if (!url) {
                resolve(null);
                return;
            }
            const image = new Image();
            if (!/^data:|^blob:/i.test(url)) {
                image.crossOrigin = 'anonymous';
            }
            image.onload = () => resolve(image);
            image.onerror = () => resolve(null);
            image.src = url;
        });
    }
    drawImageCover(ctx, image, width, height) {
        const imageWidth = image.naturalWidth || image.width;
        const imageHeight = image.naturalHeight || image.height;
        const imageRatio = imageWidth / imageHeight;
        const canvasRatio = width / height;
        let drawWidth = width;
        let drawHeight = height;
        if (imageRatio > canvasRatio) {
            drawWidth = height * imageRatio;
        } else {
            drawHeight = width / imageRatio;
        }
        ctx.drawImage(image, 0, 0, imageWidth, imageHeight, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
    }
    drawStudioPreset(ctx, width, height) {
        const palette = this.getStudioPalette();
        const wallGradient = ctx.createLinearGradient(0, 0, 0, height);
        wallGradient.addColorStop(0, palette.wallTop);
        wallGradient.addColorStop(0.48, palette.wallBottom);
        wallGradient.addColorStop(0.48, palette.floorTop);
        wallGradient.addColorStop(1, palette.floorBottom);
        ctx.fillStyle = wallGradient;
        ctx.fillRect(0, 0, width, height);

        const lightGradient = ctx.createRadialGradient(width / 2, height * 0.28, 12, width / 2, height * 0.28, width * 0.42);
        lightGradient.addColorStop(0, palette.light);
        lightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = lightGradient;
        ctx.fillRect(0, 0, width, height);

        ctx.strokeStyle = palette.shadow.replace(/[\d.]+\)$/, '0.18)');
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, height * 0.48);
        ctx.lineTo(width, height * 0.48);
        ctx.stroke();

        const shadowGradient = ctx.createRadialGradient(width / 2, height * 0.82, 8, width / 2, height * 0.82, width * 0.34);
        shadowGradient.addColorStop(0, palette.shadow);
        shadowGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = shadowGradient;
        ctx.fillRect(0, 0, width, height);
    }
    drawAdjustedStudioBackground(ctx, width, height, drawContent) {
        const adjust = this.studioBackgroundAdjust;
        ctx.save();
        ctx.translate(width / 2 + adjust.x, height / 2 + adjust.y);
        ctx.scale(adjust.scale, adjust.scale);
        ctx.translate(-width / 2, -height / 2);
        ctx.filter = `brightness(${adjust.brightness / 100}) contrast(${adjust.contrast / 100})`;
        drawContent();
        ctx.restore();
    }
    async drawStudioBackground(ctx, width, height) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        if (this.studioBackground) {
            const image = await this.loadImage(this.studioBackground);
            if (image) {
                this.drawAdjustedStudioBackground(ctx, width, height, () => {
                    this.drawImageCover(ctx, image, width, height);
                });
                return;
            }
        }
        this.drawAdjustedStudioBackground(ctx, width, height, () => {
            this.drawStudioPreset(ctx, width, height);
        });
    }
    async do() {
        let preCanvas = document.createElement('canvas'),
            preCtx = preCanvas.getContext('2d');
        preCanvas.width = this.preview.width;
        preCanvas.height = this.preview.height;
        preCtx.drawImage(this.preview, 0, 0);

        //寻找图像边界
        let minx = -1,
            miny = -1,
            maxx = -1,
            maxy = -1,
            imgData = preCtx.getImageData(
                0,
                0,
                preCanvas.width,
                preCanvas.height
            ),
            preInfo = {};
        for (let y = 0; y < preCanvas.height; y++) {
            for (let x = 0; x < preCanvas.width; x++) {
                let i = y * preCanvas.width + x;
                if (imgData.data[4 * i + 3]) {
                    if (minx + miny + maxx + maxy === -4) {
                        minx = x;
                        miny = y;
                        maxx = x;
                        maxy = y;
                    }

                    if (x < minx) minx = x;
                    if (y < miny) miny = y;
                    if (x > maxx) maxx = x;
                    if (y > maxy) maxy = y;
                    imgData.data[4 * i] = 255 - imgData.data[4 * i];
                    imgData.data[4 * i + 1] = 255 - imgData.data[4 * i + 1];
                    imgData.data[4 * i + 2] = 255 - imgData.data[4 * i + 2];
                }
            }
        }
        minx -= 50;
        maxx += 50;
        miny -= 50;
        maxy += 50;

        minx = minx < 0 ? 0 : minx;
        maxx = maxx > preCanvas.width ? preCanvas.width : maxx;
        miny = miny < 0 ? 0 : miny;
        maxy = maxy > preCanvas.height ? preCanvas.height : maxy;

        preInfo = { x: minx, y: miny, width: maxx - minx, height: maxy - miny };
        //根据图像边界绘制图片
        let resCanvas = document.createElement('canvas'),
            resCtx = resCanvas.getContext('2d'),
            rImg = preInfo.width / preInfo.height,
            drawWidth,
            drawHeight;
        resCanvas.width = this.width;
        resCanvas.height = this.height;
        if (this.editorType === 'studio') {
            await this.drawStudioBackground(resCtx, resCanvas.width, resCanvas.height);
        }
        if (rImg < this.width / this.height) {
            drawWidth = this.height * rImg;
            drawHeight = this.height;
        } else {
            drawWidth = this.width;
            drawHeight = this.width / rImg;
        }
        resCtx.drawImage(
            preCanvas,
            preInfo.x,
            preInfo.y,
            preInfo.width,
            preInfo.height,
            this.width / 2 - drawWidth / 2,
            this.height / 2 - drawHeight / 2,
            drawWidth,
            drawHeight
        );

        return resCanvas.toDataURL();
    }
}

export default CreateImageCommand;
